(function (global) {
  'use strict';

  const KEYS = Object.freeze({
    shipmentType: 'LASER Customs - Shipment type',
    destination: 'LASER Customs - Intended destination',
    company: 'LASER Customs - Registered company name',
    eoriStatus: 'LASER Customs - EORI status',
    eori: 'LASER Customs - EORI number',
    vat: 'LASER Customs - VAT number',
    contactMode: 'LASER Customs - Clearance contact',
    contactName: 'LASER Customs - Contact name',
    email: 'LASER Customs - Contact email',
    phone: 'LASER Customs - Contact phone',
    acknowledgement: 'LASER Customs - Contact acknowledgement',
    verification: 'LASER Customs - Verification',
    schema: 'LASER Customs - Schema',
  });
  const clean = (value) => String(value ?? '').trim();
  const choice = (value, options, fallback = 'unknown') => options.includes(value) ? value : fallback;
  const isBusinessImport = (data) => data.shipmentType === 'business' && ['eu', 'uk'].includes(data.destination);

  function attributesFor(data) {
    const attributes = Object.fromEntries(Object.values(KEYS).map((key) => [key, '']));
    const shipmentType = choice(data.shipmentType, ['business', 'personal', 'unknown']);
    const destination = choice(data.destination, ['eu', 'uk', 'other', 'unknown']);
    attributes[KEYS.shipmentType] = shipmentType;
    attributes[KEYS.destination] = destination;
    attributes[KEYS.schema] = 'v1';
    if (!isBusinessImport({ shipmentType, destination })) return attributes;

    attributes[KEYS.company] = clean(data.company);
    attributes[KEYS.eoriStatus] = choice(data.eoriStatus, ['provided', 'none', 'unsure'], '');
    attributes[KEYS.eori] = data.eoriStatus === 'provided' ? clean(data.eori) : '';
    attributes[KEYS.vat] = clean(data.vat);
    attributes[KEYS.contactMode] = data.separateContact ? 'separate' : 'same';
    if (data.separateContact) {
      attributes[KEYS.contactName] = clean(data.contactName);
      attributes[KEYS.email] = clean(data.email);
      attributes[KEYS.phone] = clean(data.phone);
    }
    attributes[KEYS.acknowledgement] = data.acknowledgement ? 'Acknowledged' : '';
    attributes[KEYS.verification] = 'Customer supplied - not verified';
    return attributes;
  }

  function validate(data) {
    if (!isBusinessImport(data)) return [];
    const errors = [];
    const add = (field, message) => errors.push({ field, message });
    if (!clean(data.company)) add('company', 'Enter the full registered company name.');
    if (!['provided', 'none', 'unsure'].includes(data.eoriStatus)) add('eoriStatus', 'Choose your EORI status.');
    if (data.eoriStatus === 'provided' && !clean(data.eori)) add('eori', 'Enter the EORI number you have.');
    if (data.separateContact) {
      if (!clean(data.contactName)) add('contactName', 'Enter the clearance contact name.');
      if (!clean(data.email) && !clean(data.phone)) add('email', 'Enter an email address or phone number for the clearance contact.');
      if (clean(data.email) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean(data.email))) add('email', 'Check the clearance contact email address.');
    }
    const limits = { company: 200, vat: 64 };
    if (data.eoriStatus === 'provided') limits.eori = 64;
    if (data.separateContact) Object.assign(limits, { contactName: 120, email: 254, phone: 64 });
    for (const [field, max] of Object.entries(limits)) {
      if (clean(data[field]).length > max) add(field, 'Shorten this field to ' + max + ' characters or fewer.');
    }
    if (!data.acknowledgement) add('acknowledgement', 'Confirm that the supplied contact may be contacted about clearance.');
    return errors;
  }

  async function persist(request, updateUrl, attributes) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      // Only our attributes are sent. Cart note, line items and other attributes
      // are intentionally absent from this partial cart update.
      const response = await request(updateUrl, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }), signal: controller.signal,
      });
      if (!response.ok) throw new Error('SAVE_NOT_CONFIRMED');
      const cart = await response.json();
      if (!cart || !cart.attributes || Object.entries(attributes).some(([key, value]) => clean(cart.attributes[key]) !== value)) throw new Error('SAVE_NOT_CONFIRMED');
      return cart.attributes;
    } finally { clearTimeout(timer); }
  }

  global.LaserCustomsV1 = Object.freeze({ KEYS, isBusinessImport, attributesFor, validate, persist });
  // Reuse this theme's cartUpdate event; do not intercept fetch or change cart.js.
  // Shopify retains cart attributes after the last line is removed. Invalidate
  // only our namespace whenever that empty cart is observed, including pages
  // with the active notification but no customs form.
  if (global.document && !global.laserCustomsLifecycle) {
    const bridge = global.document.querySelector('script[data-laser-customs-cart-count]');
    if (bridge) {
      const lifecycle = { count: Number(bridge.dataset.laserCustomsCartCount), pending: null, failed: false };
      global.laserCustomsLifecycle = lifecycle;
      const empty = async (hasDetails) => {
        if (!hasDetails || lifecycle.pending) return lifecycle.pending;
        const attributes = Object.fromEntries(Object.values(KEYS).map(key => [key, '']));
        global.document.dispatchEvent(new CustomEvent('laser-customs-empty'));
        const priorSave = global.document.querySelector('laser-customs-details')?.pendingSave;
        lifecycle.pending = Promise.resolve(priorSave).catch(() => {}).then(() => persist(global.fetch.bind(global), bridge.dataset.laserCustomsUpdateUrl, attributes))
          .then(() => { lifecycle.failed = false; })
          .catch(() => { lifecycle.failed = true; })
          .finally(() => {
            lifecycle.pending = null;
            global.document.dispatchEvent(new CustomEvent('laser-customs-reset-complete', { detail: { failed: lifecycle.failed } }));
          });
        return lifecycle.pending;
      };
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          const cart = event.cartData;
          if (!cart || typeof cart.item_count !== 'number') return;
          lifecycle.count = cart.item_count;
          const widget = global.document.querySelector('laser-customs-details');
          if (cart.item_count === 0) return empty(cart.attributes?.[KEYS.schema] === 'v1' || widget?.saved || widget?.dirty);
        });
      }
      if (lifecycle.count === 0 && bridge.dataset.laserCustomsSaved === 'v1') {
        global.queueMicrotask(() => empty(true));
      }
    }
  }
  if (!global.HTMLElement || !global.customElements || global.customElements.get('laser-customs-details')) return;

  class LaserCustomsDetails extends global.HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.messages = new Map(Array.from(this.querySelectorAll('[data-customs-message]'), node => [node.dataset.customsMessage, node.textContent]));
      this.status = this.querySelector('[data-customs-status]');
      this.error = this.querySelector('[data-customs-error]');
      this.saveButton = this.querySelector('[data-customs-save]');
      this.clearButton = this.querySelector('[data-customs-clear]');
      this.saved = this.dataset.saved === 'v1';
      this.dirty = false;
      this.saveButton.disabled = false;
      this.clearButton.hidden = !this.saved;
      this.addEventListener('input', () => this.onEdit());
      this.addEventListener('change', () => this.onEdit());
      this.saveButton.addEventListener('click', () => this.save());
      this.clearButton.addEventListener('click', () => this.clear());
      this.emptyListener = () => {
        this.resetFields();
        this.syncNative(Object.fromEntries(Object.values(KEYS).map(key => [key, ''])));
        this.saved = false;
        this.dirty = false;
        this.clearButton.hidden = true;
        this.setStatus('The cart was emptied. Previous customs details and contact confirmation no longer apply.');
      };
      this.resetListener = event => {
        if (event.detail.failed) {
          this.error.hidden = false;
          this.error.textContent = this.text('Previous customs details could not be cleared. Reload and remove them before using this cart for a new shipment.');
        } else this.emptyListener();
      };
      global.document.addEventListener('laser-customs-empty', this.emptyListener);
      global.document.addEventListener('laser-customs-reset-complete', this.resetListener);
      this.toggleFields();
      this.setStatus(this.saved ? 'Details already saved in this cart. Check the contact confirmation again before saving changes.' : '');
      this.hashListener = () => {
        if (global.location.hash !== '#' + this.id) return;
        this.querySelector('details').open = true;
        this.querySelector('summary').focus();
        this.scrollIntoView({ block: 'start' });
      };
      global.addEventListener('hashchange', this.hashListener);
      this.hashListener();
    }

    disconnectedCallback() {
      global.removeEventListener('hashchange', this.hashListener);
      global.document.removeEventListener('laser-customs-empty', this.emptyListener);
      global.document.removeEventListener('laser-customs-reset-complete', this.resetListener);
      this.initialized = false;
    }

    resetFields() {
      for (const field of this.querySelectorAll('[data-customs-field]')) {
        if (field.type === 'checkbox') field.checked = false;
        else field.value = ['shipmentType','destination'].includes(field.dataset.customsField) ? 'unknown' : '';
      }
      this.toggleFields();
    }

    field(name) { return this.querySelector('[data-customs-field="' + name + '"]'); }
    draft() {
      return Object.fromEntries(['shipmentType','destination','company','eoriStatus','eori','vat','separateContact','contactName','email','phone','acknowledgement'].map((name) => {
        const field = this.field(name);
        return [name, field.type === 'checkbox' ? field.checked : field.value];
      }));
    }
    syncNative(attributes) {
      for (const input of this.querySelectorAll('[data-customs-attribute]')) input.value = attributes[input.dataset.customsAttribute] ?? '';
    }
    toggleFields() {
      const data = this.draft();
      const businessImport = isBusinessImport(data);
      for (const [selector, enabled] of [['[data-customs-business]', businessImport], ['[data-customs-eori]', businessImport && data.eoriStatus === 'provided'], ['[data-customs-contact]', businessImport && data.separateContact]]) {
        const region = this.querySelector(selector);
        region.hidden = !enabled;
        // Fieldsets disable only their own visible controls. Native hidden
        // payload fields are outside them and clear obsolete customs values.
        region.disabled = !enabled;
      }
    }
    text(message) {
      const length = message.match(/^Shorten this field to (\d+) characters or fewer\.$/);
      if (length) return (this.messages.get('Shorten this field to %count% characters or fewer.') || message).replace('%count%', length[1]);
      return this.messages.get(message) || message;
    }
    setStatus(message) { this.status.textContent = this.text(message); }
    showErrors(errors) {
      for (const field of this.querySelectorAll('[data-customs-field]')) field.removeAttribute('aria-invalid');
      this.error.hidden = errors.length === 0;
      this.error.textContent = errors.map((error) => this.text(error.message)).join(' ');
      if (errors.length) {
        this.querySelector('details').open = true;
        for (const error of errors) this.field(error.field)?.setAttribute('aria-invalid', 'true');
        this.field(errors[0].field)?.focus();
      }
    }
    onEdit() {
      if (this.saving) return;
      this.dirty = true;
      const data = this.draft();
      // Clear inactive source controls too, so toggling an option back on cannot
      // silently resurrect an EORI/contact from a different declared shipment.
      if (!isBusinessImport(data)) {
        for (const name of ['company','eoriStatus','eori','vat','contactName','email','phone']) this.field(name).value = '';
        this.field('separateContact').checked = false;
        this.field('acknowledgement').checked = false;
      } else {
        if (data.eoriStatus !== 'provided') this.field('eori').value = '';
        if (!data.separateContact) for (const name of ['contactName','email','phone']) this.field(name).value = '';
      }
      this.toggleFields();
      this.syncNative(attributesFor(this.draft()));
      this.showErrors([]);
      this.setStatus('Changes are not saved yet. Save the details before checkout. Standard cart checkout also submits the current fields.');
    }
    busy(value) {
      this.saving = value;
      this.saveButton.disabled = value;
      this.clearButton.disabled = value;
      for (const field of this.querySelectorAll('[data-customs-field]')) field.disabled = value;
      if (!value) this.toggleFields();
      this.setAttribute('aria-busy', String(value));
    }
    async save() {
      if (this.saving) return;
      if (global.laserCustomsLifecycle?.count === 0 || global.laserCustomsLifecycle?.pending) {
        this.setStatus('Add an item and wait for the previous cart details to clear before saving a new shipment.');
        return;
      }
      const data = this.draft();
      const errors = validate(data);
      this.showErrors(errors);
      if (errors.length) return;
      const attributes = attributesFor(data);
      this.syncNative(attributes);
      this.busy(true);
      this.setStatus('Saving details to this cart…');
      try {
        this.pendingSave = persist(global.fetch.bind(global), this.dataset.updateUrl, attributes);
        await this.pendingSave;
        this.saved = true;
        this.dirty = false;
        this.clearButton.hidden = false;
        this.setStatus('Saved in this cart for standard checkout. These details have not been verified.');
      } catch {
        this.setStatus('The save could not be confirmed. Reload the cart to check before retrying.');
      } finally { this.pendingSave = null; this.busy(false); }
    }
    async clear() {
      if (this.saving) return;
      const attributes = Object.fromEntries(Object.values(KEYS).map((key) => [key, '']));
      this.busy(true);
      this.setStatus('Removing the saved customs details…');
      try {
        await persist(global.fetch.bind(global), this.dataset.updateUrl, attributes);
        this.resetFields();
        this.syncNative(attributes);
        this.saved = false;
        this.dirty = false;
        this.clearButton.hidden = true;
        this.showErrors([]);
        this.setStatus('Saved customs details removed from this cart.');
      } catch {
        this.setStatus('Removal could not be confirmed. Reload the cart to check before retrying.');
      } finally { this.busy(false); }
    }
  }
  global.customElements.define('laser-customs-details', LaserCustomsDetails);
})(typeof window === 'undefined' ? globalThis : window);
